import { useForm, validators } from '@hooks';
import { useSpinnerContext, useSessionContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { useNavigate } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { Button, TextField } from '@common';
import { isApiError } from '@app-types';

const AddPayor = () => {
    const { setSpinnerShow } = useSpinnerContext();
    const { session } = useSessionContext();
    const navigate = useNavigate();

    if (!session) return null;
    const { idAccount } = session;

    const validations = {
        email: (val: string) => validators.isValidEmail(val) || { email: `Email is not valid` },
        fullName: (val: string) => validators.hasValue(val) || { fullName: `Full name is required` }
    };

    const handleSubmit = async (values: { email: string; fullName: string }) => {
        setSpinnerShow(true);
        try {
            // Check if email is already registered
            await sunboundPayProxy.getPayorCheck(values.email);
            setCustomError(`email`, `Email is already registered`);
            setSpinnerShow(false);
            return;
        } catch (error: unknown) {
            if (isApiError(error) && error.response?.status !== StatusCodes.NOT_FOUND) {
                console.error(error);
                setSpinnerShow(false);
                return;
            }
        }

        try {
            // Create the new payor
            await sunboundPayProxy.createPayor(idAccount, { ...values });
            setSpinnerShow(false);
            // Navigate back to the previous page, whence the user came
            navigate(-1);
        } catch (error) {
            console.error(error);
            setSpinnerShow(false);
        }
    };

    const { values, errors, changeHandler, submitHandler, setCustomError } = useForm(
        { email: ``, fullName: `` },
        {},
        validations,
        handleSubmit
    );

    const handleCancel = () => {
        navigate(-1);
    };

    const isFormSubmittable = values.email && values.email.trim().length > 0 &&
                              values.fullName && values.fullName.trim().length > 0;

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8">
                <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                    Add New Payor
                </h1>
            </div>

            {/* Form */}
            <form onSubmit={submitHandler} noValidate className="flex flex-col gap-4">
                {/* Email Field */}
                <TextField
                    type="email"
                    name="email"
                    placeholder="Email*"
                    value={values.email}
                    onChange={changeHandler}
                    error={errors.email}
                />

                {/* Full Name Field */}
                <TextField
                    type="text"
                    name="fullName"
                    placeholder="Full Name*"
                    value={values.fullName}
                    onChange={changeHandler}
                    error={errors.fullName}
                />

                {/* Buttons */}
                <div className="flex gap-4 items-center mt-4">
                    <Button
                        variant="secondary"
                        size="lg"
                        className="flex-1"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="flex-1"
                        disabled={!isFormSubmittable}
                    >
                        Add Payor
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default AddPayor;
